import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users,
  ChefHat,
  Truck,
  User,
  Settings,
  LogOut,
  Eye,
  ArrowRight
} from 'lucide-react';

// Import all dashboard components
import OwnerDashboard from './owner/OwnerDashboardNew';
import AdminDashboard from './admin/AdminDashboard';
import KitchenDashboard from './kitchen/KitchenDashboard';
import DriverDashboard from './driver/DriverDashboard';
import PackagingDashboard from './packaging/PackagingDashboard';
import CustomerDashboard from './customer/CustomerDashboardNew';
import TruckerDashboardComponent from './trucker/TruckerDashboard';

const DashboardRouter = () => {
  const { user, logout, isOwnerOrAdmin } = useAuth();
  const [viewingRole, setViewingRole] = useState(user?.role || 'owner');
  const [showRoleSelector, setShowRoleSelector] = useState(false);

  // Dashboard configuration
  const dashboardConfig = {
    owner: {
      component: OwnerDashboard,
      name: 'Owner Dashboard',
      description: 'Business analytics, metrics, and administrative controls',
      icon: Users,
      color: 'bg-purple-500'
    },
    admin: {
      component: AdminDashboard,
      name: 'Admin Dashboard',
      description: 'Customer management and administrative functions',
      icon: Settings,
      color: 'bg-blue-500'
    },
    kitchen: {
      component: KitchenDashboard,
      name: 'Kitchen Dashboard',
      description: 'Food preparation and meal planning',
      icon: ChefHat,
      color: 'bg-[#FF6A00]'
    },
    driver: {
      component: DriverDashboard,
      name: 'Driver Dashboard',
      description: 'Delivery management and route optimization',
      icon: Truck,
      color: 'bg-green-500'
    },
    packaging: {
      component: PackagingDashboard,
      name: 'Packaging Dashboard',
      description: 'Route management and packaging control',
      icon: Settings,
      color: 'bg-amber-500'
    },
    customer: {
      component: CustomerDashboard,
      name: 'Customer Dashboard',
      description: 'Account management and meal plan tracking',
      icon: User,
      color: 'bg-indigo-500'
    },
    trucker: {
      component: TruckerDashboardComponent,
      name: 'Trucker Dashboard',
      description: 'Trucker meal plan and delivery management',
      icon: Truck,
      color: 'bg-orange-500'
    },
    manager: {
      component: AdminDashboard,
      name: 'Manager Dashboard',
      description: 'Operational oversight and management',
      icon: Users,
      color: 'bg-orange-600'
    },
    Manager: {
      component: AdminDashboard,
      name: 'Manager Dashboard',
      description: 'Operational oversight and management',
      icon: Users,
      color: 'bg-orange-600'
    }
  };

  // Role-based access control
  const getRoleAccess = () => {
    const role = user?.role;
    switch (role) {
      case 'owner':
        return ['owner', 'admin', 'kitchen', 'driver', 'packaging'];
      case 'admin':
        return ['admin', 'kitchen', 'driver', 'packaging'];
      case 'manager':
      case 'Manager':
        return ['admin', 'kitchen', 'driver', 'packaging'];
      case 'kitchen':
        return ['kitchen', 'packaging'];
      case 'driver':
        return ['driver'];
      case 'customer':
        return ['customer'];
      case 'trucker':
        return ['trucker'];
      default:
        return [role];
    }
  };

  const availableRoles = getRoleAccess();

  const DashboardHeader = () => (
    <div className="flex justify-between items-center mb-6">
      <div>
        <h1 className="text-3xl font-bold text-black">Just Protein</h1>
        <div className="w-16 h-1 bg-#FF6A00 mt-2"></div>
        <div className="flex items-center space-x-4 mt-2">
          <p className="text-[#3C3C3C]">Multi-Role Dashboard System</p>
          {isOwnerOrAdmin && (
            <Badge variant="outline" className="text-xs">
              Demo Mode - All Dashboards Available
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <Badge variant="outline" className="px-3 py-1">
          {user?.role?.toUpperCase()}
        </Badge>
        <span className="text-sm text-[#3C3C3C]">{user?.email}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  // Role Selection Interface
  if (showRoleSelector && isOwnerOrAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <DashboardHeader />

        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>Select Dashboard View</CardTitle>
            <CardDescription>
              Choose which role's dashboard you want to view. As an owner, you have access to all dashboard types.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availableRoles.map((role) => {
                const config = dashboardConfig[role];
                const Icon = config.icon;

                return (
                  <Card
                    key={role}
                    className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-orange-200"
                    onClick={() => {
                      setViewingRole(role);
                      setShowRoleSelector(false);
                    }}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-12 h-12 ${config.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="font-semibold mb-2">{config.name}</h3>
                      <p className="text-sm text-[#3C3C3C] mb-4">{config.description}</p>
                      <Button size="sm" className="w-full">
                        <Eye className="h-4 w-4 mr-2" />
                        View Dashboard
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              <Button
                variant="outline"
                onClick={() => setShowRoleSelector(false)}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render selected dashboard
  const currentConfig = dashboardConfig[viewingRole];
  const CurrentDashboard = currentConfig.component;

  // Callback for dashboard components to switch views
  const handleSwitchDashboard = (newRole) => {
    if (newRole === 'selector') {
      setShowRoleSelector(true);
      return;
    }

    // Owners can switch to any dashboard. Others must have explicit permission.
    if (dashboardConfig[newRole]) {
      if (user?.role === 'owner' || user?.dashboards?.includes(newRole)) {
        setViewingRole(newRole);
        setShowRoleSelector(false); // Ensure selector is hidden
      }
    }
  };

  return (
    <div className="relative">
      {/* Render the selected dashboard with callback */}
      <CurrentDashboard onSwitchDashboard={handleSwitchDashboard} />
    </div>
  );
};

export default DashboardRouter;